import Line from "../Line";
import Context from "../Context";
import {STATE, GRAMMAR, BLOCK} from "../Type";
// NOTE:
// This import will help to break the circular dependency of between Block and its inheritances
import {
  Action,
  GlobalAction,
  Callback,
  FunctionBlock,
  Operator,
  Property,
  Variable,
  Animation,
  Axis,
  BooleanBlock,
  Constant,
  Direction,
  Key,
  NumberBlock,
  Position,
  Random,
  Scene,
  StringBlock,
  Time,
  Touch,
  DPad,
  GameObject,
  Joystick,
  Screen,
  Sound,
  Sprite,
  Text,
  Mobile,
  Ranking,
  Timer,
  Util,
} from "./index";

export class Block {
  constructor(props = {}) {
    this.id = props.id || Context.generateId();
    this.parent = props.parent;
    this.state = props.state;
    this.grammar = props.grammar;
    this.data = props.data;
    this.children = props.children;
    this.uuid = props.uuid;
  }

  get type() {
    return "Block";
  }

  get category() {
    return null;
  }

  clone(parent) {
    const clone = Object.assign(Object.create(Object.getPrototypeOf(this)), this);
    clone.id = Context.generateId();
    clone.parent = parent;
    clone.children = clone.children && this.children.map(child => child.clone(clone));
    return clone;
  }

  isEqual(block) {
    if (!(block instanceof Block)) {
      return false;
    }

    return this.type === block.type && this.data === block.data;
  }

  createInstanceBlockWith(prototypeBlock) {
    const instanceBlock = Object.assign(Object.create(Object.getPrototypeOf(prototypeBlock)), prototypeBlock);
    instanceBlock.id = Context.generateId();
    instanceBlock.state = STATE.INSTANCE;
    instanceBlock.parent = this.parent;
    instanceBlock.grammar = instanceBlock.getGrammarFor(this.parent);
    instanceBlock.children = instanceBlock.getDefaultChildren();
    return instanceBlock;
  }

  getGrammarFor(parent) {
    // inherit
  }

  getDefaultChildren() {
    // inherit
    return [];
  }

  isCompatibleWith(instanceBlock) {
    if (!this.children || this.children.length < 1) return true;

    if (this.children.length !== instanceBlock.children.length) return false;

    for (let i in this.children) {
      const childToMaintain = this.children[i];
      if (!childToMaintain.data) continue;
      if (!instanceBlock.children[i].checkBlockReplaceable(childToMaintain) || childToMaintain.isDisabledFor(instanceBlock.children[i])) {
        return false;
      }
    }

    if (this.children[0] instanceof Action) {
      const action = this.children[0];
      const objectives = action.children || [];
      action.parent = instanceBlock;
      for (let i in objectives) {
        const objective = objectives[i];
        if (!objective.data) continue;
        if (!objective.checkBlockReplaceable(objective)) {
          action.parent = this;
          return false;
        }
      }
      action.parent = this;
    }

    return true;
  }

  coverWith(instanceBlock) {
    if (this.parent instanceof Line) {
      this.parent.block = instanceBlock;
    } else {
      for (let i in this.parent.children) {
        if (this.parent.children[i] === this) {
          this.parent.children[i] = instanceBlock;
        }
      }
    }
    return instanceBlock;
  }

  replaceWith(instanceBlock) {
    if (this.parent instanceof Line) {
      this.parent.block = instanceBlock;
    } else {
      for (let i in this.parent.children) {
        if (this.parent.children[i] === this) {
          this.parent.children[i] = instanceBlock;
        }
      }
    }
    if (this.children && this.children.length > 0) {
      instanceBlock.children = this.children;
      for (let i in instanceBlock.children) {
        instanceBlock.children[i].parent = instanceBlock;
      }
    }
    return instanceBlock;
  }

  static getPrototypeBlocks() {
    return [];
  }

  filterPrototypeBlocksReplaceable(prototypeBlocks) {
    return prototypeBlocks.filter(block => this.checkBlockReplaceable(block));
  }

  checkBlockReplaceable(block) {
    if (!this.parent.isAvailableParentFor(block)) {
      return false;
    }
    if (this.parent instanceof Line) {
      return this.parent.checkAvailableForRootBlock(block);
    } else if (this.parent instanceof Block) {
      const childIndex = this.parent.children.indexOf(this);
      return this.parent.checkChildAvailableAtIndex(block, childIndex);
    }
  }

  checkChildAvailableAtIndex(child, index) {
    if (child instanceof Operator) {
      if (this instanceof Operator) {
        return false;
      }
      const operatorTypes = child.getOperatorTypes();
      const finalChildTypes = this.getFinalChildTypesAt(index);
      for (let i in finalChildTypes) {
        if (operatorTypes.includes(finalChildTypes[i])) {
          return true;
        }
      }
      return false;
    } else {
      const availableChildTypes = this.getAvailableChildTypesAt(index);
      for (let i in availableChildTypes) {
        if (child instanceof availableChildTypes[i]) {
          return true;
        }
      }
      return false;
    }
  }

  getAvailableChildTypesAt(index, grammar) {
    return [];
  }

  getFinalChildTypesAt(index) {
    return [];
  }

  isAvailableParentFor(block) {
    const availableParentTypes = block.getAvailableParentTypes();
    for (let i in availableParentTypes) {
      if (this instanceof availableParentTypes[i]) {
        return true;
      }
    }
    return false;
  }

  getAvailableParentTypes() {
    return [Line, Block];
  }

  filterPrototypeBlocksDisabled(prototypeBlocks) {
    return prototypeBlocks.filter(block => block.isDisabledFor(this));
  }

  isDisabledFor(block) {
    return false;
  }

  findChild(where) {
    if (!this.children) return;

    for (let i = 0; i < this.children.length; i++) {
      const child = this.children[i];
      if (child.match(where)) {
        return child;
      }
    }
    for (let i = 0; i < this.children.length; i++) {
      const child = this.children[i];
      const grandChild = child.findChild(where);
      if (grandChild) {
        return grandChild;
      }
    }
  }

  findParent(where) {
    if (!this.parent) return;
    if (this.parent.match(where)) {
      return this.parent;
    } else {
      return this.parent.findParent(where);
    }
  }

  findNextEmptyBlock() {
    const whereEmpty = {type: BLOCK.BLOCK};
    const parentLine = this.getParentLine();
    const blocks = [];
    Context.traverse(parentLine, {
      onBlock(block) {
        if (block.match(whereEmpty)) {
          blocks.push(block);
        }
      },
    });
    if (blocks.length > 0) {
      return blocks[0];
    } else {
      const nextLine = parentLine.getNextDisplayLine();
      if (nextLine.block instanceof Block) {
        return nextLine.block;
      } else {
        return;
      }
    }
  }

  match(where) {
    const {type, id, state, grammar, data} = where;
    if (type && type !== this.type) return false;
    if (id && id !== this.id) return false;
    if (state && state !== this.state) return false;
    if (grammar && grammar !== this.grammar) return false;
    if (data && data !== this.data) return false;
    return true;
  }

  getParentLine() {
    return this.findParent({type: "Line"});
  }

  getParentContext() {
    return this.findParent({type: "Context"});
  }

  static getConstructorByName(name) {
    return {
      Block: Block,
      GameObject: GameObject,
      Sprite: Sprite,
      Screen: Screen,
      Joystick: Joystick,
      DPad: DPad,
      Text: Text,
      Sound: Sound,
      Property: Property,
      Action: Action,
      GlobalAction: GlobalAction,
      Constant: Constant,
      NumberBlock: NumberBlock,
      StringBlock: StringBlock,
      BooleanBlock: BooleanBlock,
      Key: Key,
      Direction: Direction,
      Animation: Animation,
      Position: Position,
      Touch: Touch,
      Random: Random,
      Scene: Scene,
      Axis: Axis,
      Time: Time,
      Util: Util,
      Mobile: Mobile,
      Ranking: Ranking,
      Timer: Timer,
      Variable: Variable,
      Operator: Operator,
      Callback: Callback,
      FunctionBlock: FunctionBlock,
    }[name];
  }

  static getCompatibleJSONFrom(json, parent) {
    const compatibleJSON = {
      constructor: json.constructor,
      state: json.state,
      grammar: json.grammar,
      data: json.data,
      children: json.children,
      uuid: json.uuid,
    };
    if (json.blockType) {
      compatibleJSON.constructor = json.blockType[0].toUpperCase() + json.blockType.slice(1, json.blockType.length);
    }
    if (json.blockType === "sprite") {
      compatibleJSON.constructor = json.dataType[0].toUpperCase() + json.dataType.slice(1, json.dataType.length);
    }
    if (json.blockType === "util" && ["if", "else if", "else", "repeat"].includes(json.data)) {
      compatibleJSON.grammar = GRAMMAR.PHRASE;
    }
    if (json.blockType === "constant") {
      if (parent instanceof Action && parent.data === "playAnimation") {
        compatibleJSON.constructor = BLOCK.ANIMATION;
      } else if (["up", "down", "left", "right"].includes(json.data)) {
        compatibleJSON.constructor = BLOCK.DIRECTION;
      } else if (["x", "y"].includes(json.data)) {
        compatibleJSON.constructor = BLOCK.AXIS;
      } else if (json.data === "touch") {
        compatibleJSON.constructor = BLOCK.TOUCH;
      } else if (json.data === "random") {
        compatibleJSON.constructor = BLOCK.RANDOM;
      } else if (json.data instanceof Object) {
        compatibleJSON.constructor = BLOCK.POSITION;
      } else if (["true", "false"].includes(json.data)) {
        compatibleJSON.constructor = BLOCK.BOOLEANBLOCK;
      } else if (isNaN(json.data)) {
        compatibleJSON.constructor = BLOCK.STRINGBLOCK;
      } else {
        compatibleJSON.constructor = BLOCK.NUMBERBLOCK;
      }
    }
    if (json.childs) {
      compatibleJSON.children = json.childs;
    }
    if (json.mode) {
      compatibleJSON.state = json.mode;
    }
    if (compatibleJSON.constructor === BLOCK.CALLBACK && json.data) {
      compatibleJSON.children = json.data;
    }
    return compatibleJSON;
  }

  static fromJSON(json, parent) {
    const compatibleJSON = Block.getCompatibleJSONFrom(json, parent);
    const constructorName = compatibleJSON.constructor;
    const Constructor = Block.getConstructorByName(constructorName) || Block;
    const block = new Constructor({
      ...compatibleJSON,
      state: STATE.INSTANCE,
      parent,
    });
    if (compatibleJSON.children) {
      if (constructorName === BLOCK.CALLBACK) {
        block.children = compatibleJSON.children.map(child => Line.fromJSON(child, block));
      } else {
        block.children = compatibleJSON.children.map(child => Block.fromJSON(child, block));
      }
    }
    return block;
  }

  toJSON() {
    return {
      constructor: this.type,
      grammar: this.grammar,
      data: this.data,
      uuid: this.uuid,
      children: this.children ? this.children.map(child => child.toJSON()) : undefined,
    };
  }

  toJavascript() {
    return "";
  }

  linesToJavascript(lines) {
    let code = "";
    for (let i in lines) {
      const line = lines[i];
      const lineCode = line.toJavascript();
      if (lineCode) {
        if (code !== "") {
          code += "\n";
        }
        code += "\t";
        code += lineCode;
      }
    }
    return code;
  }
}
